import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";

function splitWarningLines(value) {
  return String(value ?? "")
    .split(/\n|•|;/g)
    .map((part) => part.trim())
    .filter(Boolean);
}

function PartyBlock({ name, address, email }) {
  return (
    <>
      {name ? (
        <p className="CreateGpsrLabelPage__gpsrCompactText">{name}</p>
      ) : null}
      {address ? (
        <p className="CreateGpsrLabelPage__gpsrCompactText">{address}</p>
      ) : null}
      {email ? (
        <p className="CreateGpsrLabelPage__gpsrCompactText">{email}</p>
      ) : null}
    </>
  );
}

function LabelSection({
  title,
  children,
  showDivider = true,
  className = "",
}) {
  return (
    <section
      className={`CreateGpsrLabelPage__gpsrCompactSection${
        className ? ` ${className}` : ""
      }`}
    >
      <div className="CreateGpsrLabelPage__gpsrCompactSectionContent">
        {title ? (
          <h4 className="CreateGpsrLabelPage__gpsrCompactSectionTitle">
            {title}
          </h4>
        ) : null}
        {children}
      </div>
      {showDivider ? (
        <div
          className="CreateGpsrLabelPage__gpsrCompactDivider"
          aria-hidden="true"
        />
      ) : null}
    </section>
  );
}

function EmptyCell({ showDivider = false }) {
  return (
    <div
      className="CreateGpsrLabelPage__gpsrCompactSection CreateGpsrLabelPage__gpsrCompactSection--empty"
      aria-hidden="true"
    >
      {showDivider ? (
        <div
          className="CreateGpsrLabelPage__gpsrCompactDivider"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}

function QrCell({ qrCodeValue, showDivider = false }) {
  return (
    <div className="CreateGpsrLabelPage__gpsrCompactQrCell">
      <div className="CreateGpsrLabelPage__gpsrCompactQr">
        {qrCodeValue ? (
          <QRCodeSVG
            value={qrCodeValue}
            size={218}
            level="M"
            includeMargin={false}
          />
        ) : null}
      </div>
      {showDivider ? (
        <div
          className="CreateGpsrLabelPage__gpsrCompactDivider"
          aria-hidden="true"
        />
      ) : null}
    </div>
  );
}

function BrandLogoBlock({ logoUrl, onError }) {
  return (
    <div className="CreateGpsrLabelPage__gpsrCompactLogoFrame">
      <img
        src={logoUrl}
        alt=""
        className="CreateGpsrLabelPage__gpsrCompactLogoImage"
        onError={onError}
      />
    </div>
  );
}

function buildGpsrPrintableRows(contentBlocks) {
  const rows = [];
  let index = 0;

  while (index + 1 < contentBlocks.length) {
    rows.push({
      left: contentBlocks[index],
      right: contentBlocks[index + 1],
    });
    index += 2;
  }

  if (index < contentBlocks.length) {
    rows.push({
      left: contentBlocks[index],
      right: { type: "qr" },
    });
  } else {
    rows.push({
      left: null,
      right: { type: "qr" },
    });
  }

  return rows;
}

function renderContentBlock(block, showDivider) {
  if (!block) {
    return <EmptyCell showDivider={showDivider} />;
  }

  if (block.type === "logo") {
    return (
      <LabelSection
        showDivider={showDivider}
        className="CreateGpsrLabelPage__gpsrCompactSection--logo"
      >
        <BrandLogoBlock logoUrl={block.logoUrl} onError={block.onError} />
      </LabelSection>
    );
  }

  return (
    <LabelSection title={block.title} showDivider={showDivider}>
      {block.content}
    </LabelSection>
  );
}

const DEFAULT_GPSR_PRINTABLE_COPY = {
  labelTitle: "Product Label (GPSR)",
  productTraceability: "Product Traceability",
  manufacturer: "Manufacturer / Brand Owner",
  authorizedRepresentative: "Authorized Representative",
  importer: "Importer",
  fibreComposition: "Fibre Composition",
  warningsAndInstructions: "Warnings and Instructions",
  generatedBy: "generated by Lappa.org",
};

export default function GpsrPrintableLabel({
  productFields = [],
  productDetailFields = [],
  manufacturerName = "",
  manufacturerAddress = "",
  manufacturerEmail = "",
  showEuRepresentative = false,
  euRepresentativeName = "",
  euRepresentativeAddress = "",
  euRepresentativeEmail = "",
  showImporter = false,
  importerName = "",
  importerAddress = "",
  importerEmail = "",
  fibreComponents = [],
  careSymbols = [],
  additionalCareInstructions = "",
  showCareInstructions = false,
  safetyFields = [],
  qrCodeValue = null,
  brandLogoUrl = null,
  brandDisplayName: _brandDisplayName = "",
  isDense = false,
  labelRef = null,
  copy: copyProp = null,
}) {
  const copy = {
    ...DEFAULT_GPSR_PRINTABLE_COPY,
    ...(copyProp && typeof copyProp === "object" ? copyProp : {}),
  };

  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    setLogoFailed(false);
  }, [brandLogoUrl]);

  const hasExtraParty = showEuRepresentative || showImporter;
  const hasFibre = Array.isArray(fibreComponents) && fibreComponents.length > 0;
  const hasManufacturer =
    Boolean(manufacturerName) ||
    Boolean(manufacturerAddress) ||
    Boolean(manufacturerEmail);

  const logoUrl = String(brandLogoUrl ?? "").trim();
  const hasBrandLogo = Boolean(logoUrl) && !logoFailed;

  const traceabilityFields = [
    ...(Array.isArray(productFields) ? productFields : []),
    ...(Array.isArray(productDetailFields) ? productDetailFields : []),
  ];

  const warningItems = [];
  if (showCareInstructions) {
    if (Array.isArray(careSymbols)) {
      careSymbols.forEach((symbol) => {
        const text = String(symbol ?? "").trim();
        if (text) warningItems.push(text);
      });
    }
    splitWarningLines(additionalCareInstructions).forEach((line) => {
      warningItems.push(line);
    });
  }
  if (Array.isArray(safetyFields)) {
    safetyFields.forEach((field) => {
      splitWarningLines(field?.value).forEach((line) => {
        warningItems.push(line);
      });
    });
  }
  const hasWarnings = warningItems.length > 0;

  const contentBlocks = [];

  if (hasBrandLogo) {
    contentBlocks.push({
      type: "logo",
      key: "brand-logo",
      logoUrl,
      onError: () => setLogoFailed(true),
    });
  }

  contentBlocks.push({
    type: "section",
    key: "traceability",
    title: copy.productTraceability,
    content: (
      <>
        {traceabilityFields.map((field) => (
          <p
            key={field.label}
            className="CreateGpsrLabelPage__gpsrCompactText"
          >
            {field.label}: {field.value}
          </p>
        ))}
      </>
    ),
  });

  if (hasManufacturer) {
    contentBlocks.push({
      type: "section",
      key: "manufacturer",
      title: copy.manufacturer,
      content: (
        <PartyBlock
          name={manufacturerName}
          address={manufacturerAddress}
          email={manufacturerEmail}
        />
      ),
    });
  }

  if (showEuRepresentative) {
    contentBlocks.push({
      type: "section",
      key: "eu-representative",
      title: copy.authorizedRepresentative,
      content: (
        <PartyBlock
          name={euRepresentativeName}
          address={euRepresentativeAddress}
          email={euRepresentativeEmail}
        />
      ),
    });
  }

  if (showImporter) {
    contentBlocks.push({
      type: "section",
      key: "importer",
      title: copy.importer,
      content: (
        <PartyBlock
          name={importerName}
          address={importerAddress}
          email={importerEmail}
        />
      ),
    });
  }

  if (hasFibre) {
    contentBlocks.push({
      type: "section",
      key: "fibre",
      title: copy.fibreComposition,
      content: (
        <ul className="CreateGpsrLabelPage__gpsrCompactComponentList">
          {fibreComponents.map((component) => (
            <li key={component.id}>
              <p className="CreateGpsrLabelPage__gpsrCompactText">
                {component.name}: {component.summary}
              </p>
            </li>
          ))}
        </ul>
      ),
    });
  }

  if (hasWarnings) {
    contentBlocks.push({
      type: "section",
      key: "warnings",
      title: copy.warningsAndInstructions,
      content: (
        <div className="CreateGpsrLabelPage__gpsrCompactWarningList">
          {warningItems.map((item, index) => (
            <p
              key={`${item}-${index}`}
              className="CreateGpsrLabelPage__gpsrCompactText"
            >
              {item}
            </p>
          ))}
        </div>
      ),
    });
  }

  const rows = buildGpsrPrintableRows(contentBlocks);
  const lastRowIndex = rows.length - 1;

  return (
    <article
      ref={labelRef}
      className={`CreateGpsrLabelPage__gpsrCompactLabel${
        hasExtraParty ? " CreateGpsrLabelPage__gpsrCompactLabel--withEu" : ""
      }${isDense ? " CreateGpsrLabelPage__gpsrCompactLabel--dense" : ""}`}
      aria-label={copy.labelTitle}
    >
      <div className="CreateGpsrLabelPage__gpsrCompactLabelInner">
        <header className="CreateGpsrLabelPage__gpsrCompactHeader">
          <h3 className="CreateGpsrLabelPage__gpsrCompactLabelTitle">
            {copy.labelTitle}
          </h3>
        </header>

        <div className="CreateGpsrLabelPage__gpsrCompactLabelBody">
          {rows.map((row, rowIndex) => {
            const showDivider = rowIndex < lastRowIndex;
            const rightIsQr = row.right?.type === "qr";

            return (
              <React.Fragment key={`gpsr-row-${rowIndex}`}>
                {renderContentBlock(row.left, showDivider)}
                {rightIsQr ? (
                  <QrCell
                    qrCodeValue={qrCodeValue}
                    showDivider={showDivider}
                  />
                ) : (
                  renderContentBlock(row.right, showDivider)
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <p className="CreateGpsrLabelPage__gpsrCompactLabelGenerated">
        {copy.generatedBy}
      </p>
    </article>
  );
}
